import React, { useState, useEffect } from 'react';
import { discountService, DiscountValidationResponse } from '@/services/discount.service';
import { Tag, Check, X, Loader2 } from 'lucide-react';

interface DiscountCodeInputProps {
  productId: string;
  productType: 'course' | 'bundle' | 'digital_product';
  originalPrice: number;
  onDiscountApplied: (discountInfo: DiscountValidationResponse | null, code: string) => void;
  /** Optional initial code to pre-populate (e.g., system discount code when sale is active) */
  initialCode?: string;
}

const DiscountCodeInput: React.FC<DiscountCodeInputProps> = ({
  productId,
  productType,
  originalPrice,
  onDiscountApplied,
  initialCode
}) => {
  const [code, setCode] = useState(initialCode || '');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<DiscountValidationResponse | null>(null);
  const [error, setError] = useState('');
  const [isApplied, setIsApplied] = useState(false);
  const [hasAutoApplied, setHasAutoApplied] = useState(false);

  // Auto-apply initial code when component mounts (e.g., system discount code)
  useEffect(() => {
    if (initialCode && !hasAutoApplied && productId && productType) {
      setHasAutoApplied(true);
      setCode(initialCode);
      // Auto-validate and apply the initial code
      const autoApply = async () => {
        setIsValidating(true);
        try {
          const response = await discountService.validateDiscountCode({
            code: initialCode.trim().toUpperCase(),
            product_id: productId,
            product_type: productType
          });

          if (response.success && response.data && response.data.valid) {
            setValidationResult(response.data);
            setIsApplied(true);
            onDiscountApplied(response.data, initialCode.trim().toUpperCase());
          }
        } catch (err) {
          // Silently fail auto-apply - user can still manually enter code
          console.error('Auto-apply discount code failed:', err);
        } finally {
          setIsValidating(false);
        }
      };
      autoApply();
    }
  }, [initialCode, productId, productType, hasAutoApplied, onDiscountApplied]);

  const handleApplyCode = async () => {
    if (!code.trim()) {
      setError('Please enter a discount code');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const response = await discountService.validateDiscountCode({
        code: code.trim().toUpperCase(),
        product_id: productId,
        product_type: productType
      });

      if (response.success && response.data) {
        if (response.data.valid) {
          setValidationResult(response.data);
          setIsApplied(true);
          onDiscountApplied(response.data, code.trim().toUpperCase());
        } else {
          setError(response.data.message || 'Invalid discount code');
          setValidationResult(null);
          setIsApplied(false);
          onDiscountApplied(null, '');
        }
      } else {
        setError(response.error || 'Failed to validate code');
        setValidationResult(null);
        setIsApplied(false);
        onDiscountApplied(null, '');
      }
    } catch (err) {
      setError('Failed to validate discount code');
      setValidationResult(null);
      setIsApplied(false);
      onDiscountApplied(null, '');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveCode = () => {
    setCode('');
    setValidationResult(null);
    setError('');
    setIsApplied(false);
    onDiscountApplied(null, '');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyCode();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tag className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-300">Discount Code</span>
      </div>

      {isApplied && validationResult ? (
        // Applied state
        <div className="flex items-center justify-between p-3 sm:p-4 min-h-[52px] bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 bg-emerald-500/20 rounded-full flex-shrink-0">
              <Check className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                <span className="font-mono text-emerald-300 font-semibold text-sm sm:text-base">{code.toUpperCase()}</span>
                <span className="text-emerald-400 text-xs sm:text-sm">-{validationResult.discount_percentage}% off</span>
              </div>
              <div className="text-xs text-gray-400">
                Saving ${(originalPrice - validationResult.final_price).toFixed(2)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemoveCode}
            className="p-2 sm:p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors flex-shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center"
            title="Remove code"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        // Input state - mobile optimized
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError('');
            }}
            onKeyPress={handleKeyPress}
            placeholder="Enter code"
            className="flex-1 px-4 py-3 min-h-[48px] text-base sm:text-sm text-gray-200 bg-slate-800/60 border-2 border-blue-500/20 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 font-mono uppercase"
            disabled={isValidating}
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            type="button"
            onClick={handleApplyCode}
            disabled={isValidating || !code.trim()}
            className="px-6 sm:px-4 py-3 sm:py-2 min-h-[48px] bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isValidating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Checking...</span>
              </>
            ) : (
              'Apply'
            )}
          </button>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
};

export default DiscountCodeInput;
